<template>
  <div class="product-card" @click="goToDetail">
    <!-- 产品图片 -->
    <div class="card-image">
      <img 
        :src="getImageFullUrl(product.imageUrl) || defaultImage" 
        :alt="name" 
        @error="handleImageError"
      />
      <!-- 分类标签 -->
      <div class="category-badge" v-if="product.category">
        {{ product.category }}
      </div>
    </div>
    
    <!-- 卡片内容 -->
    <div class="card-body">
      <!-- 产品名称 -->
      <h3 class="card-title" :title="name">{{ name }}</h3>
      
      <!-- 价格 -->
      <div class="card-price-row">
        <span class="price-symbol">¥</span>
        <span class="price-amount">{{ formatPrice(product.price) }}</span>
      </div>
      
      <!-- 查看详情按钮 -->
      <button class="view-detail-btn" @click.stop="goToDetail">
        {{ locale === 'zh' ? '查看详情 →' : 'View Details →' }}
      </button>
    </div>
  </div>
</template>

<script>
import { getImageFullUrl as getFullImageUrlUtil } from '../utils/image'

export default {
  name: 'ProductCard',
  
  props: {
    product: {
      type: Object,
      required: true,
      validator(value) {
        return value && (value.id || value.id === 0)
      }
    }
  },
  
  data() {
    return {
      defaultImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2Y4ZhFhYyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOTQ5M2FiIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+8J+PuPCfmJw8L3RleHQ+PC9zdmc+'
    }
  },
  
  computed: {
    locale() {
      return this.$i18n.locale || 'zh'
    },
    
    name() {
      if (!this.product) return ''
      const zhName = this.product.nameZh || this.product.name_zh || ''
const enName = this.product.nameEn || this.product.name_en || ''
const fallbackName = this.product.name || '未命名产品'  
      
      return this.locale === 'zh' ? 
        (zhName || enName || fallbackName) : 
        (enName || zhName || fallbackName)
    }
  },
  
  methods: {
    getImageFullUrl(url) {
      return getFullImageUrlUtil(url)
    },

    goToDetail() {
      if (!this.product || !this.product.id) {
        console.warn('Product ID is missing')
        return
      }
      
      // 使用Vue Router跳转到详情页
      if (this.$router) {
        this.$router.push({
          path: `/product/${this.product.id}`,
          query: { from: 'list' }  // 可选：记录来源
        })
      } else {
        // 如果没有路由，使用window.location（会刷新页面）
        window.location.href = `/product/${this.product.id}`
      }
    },
    
    handleImageError(event) {
      console.log('Image load failed, using placeholder')
      event.target.src = this.defaultImage
    },
    
    formatPrice(price) {
      if (!price && price !== 0) return '0.00'
      
      const numPrice = typeof price === 'string' ? parseFloat(price) : price
      
      if (isNaN(numPrice)) return '0.00'
      
      return numPrice.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  }
}
</script>

<style scoped>
/* 卡片容器 */
.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--border-color, #e2e8f0);
  position: relative;
}

.product-card:hover {
  border-color: var(--primary-color, #1a56db);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

/* 图片区域 */
.card-image {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background: var(--bg-tertiary, #f1f5f9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .card-image img {
  transform: scale(1.05);
}

/* 分类标签 */
.category-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 0.25rem 0.7rem;
  background: rgba(255, 255, 255, 0.95);
  color: var(--text-secondary, #64748b);
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid var(--border-color, #e2e8f0);
  z-index: 5;
  max-width: calc(100% - 20px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 卡片内容区域 */
.card-body {
  padding: 1rem 1.15rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: white;
}

/* 产品名称 */
.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #1e293b);
  line-height: 1.4;
  margin: 0;
  min-height: 2.8em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-card:hover .card-title {
  color: var(--primary-color, #1a56db);
}

/* 价格行 */
.card-price-row {
  display: flex;
  align-items: baseline;
  gap: 0.1rem;
}

.price-symbol {
  font-size: 1rem;
  font-weight: 600;
  color: var(--accent-color, #d97706);
  line-height: 1;
}

.price-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--accent-color, #d97706);
  line-height: 1;
  letter-spacing: -0.02em;
}

/* 查看详情按钮 */
.view-detail-btn {
  width: 100%;
  padding: 0.55rem 1rem;
  background: transparent;
  border: 1px solid var(--border-color, #e2e8f0);
  color: var(--text-secondary, #64748b);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.15rem;
}

.view-detail-btn:hover {
  border-color: var(--primary-color, #1a56db);
  color: var(--primary-color, #1a56db);
  background: rgba(26, 86, 219, 0.04);
}

/* 加载状态动画 */
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.product-card.loading {
  pointer-events: none;
}

.product-card.loading .card-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

/* 响应式设计 */

/* 手机设备 */
@media (max-width: 768px) {
  .product-card {
    border-radius: 10px;
  }

  .card-title {
    font-size: 0.95rem;
  }

  .price-amount {
    font-size: 1.15rem;
  }

  .card-body {
    padding: 1rem;
    gap: 0.65rem;
  }

  .card-title {
    font-size: 0.95rem;
    min-height: 2.5em;
  }

  .category-badge {
    top: 8px;
    right: 8px;
    padding: 0.3rem 0.7rem;
    font-size: 0.7rem;
  }

  .price-symbol {
    font-size: 1.1rem;
  }

  .price-amount {
    font-size: 1.25rem;
  }

  .view-detail-btn {
    padding: 0.6rem 0.85rem;
    font-size: 0.875rem;
  }
}

/* 小屏手机 */
@media (max-width: 480px) {
  .card-body {
    padding: 0.85rem;
  }

  .card-title {
    font-size: 0.9rem;
    -webkit-line-clamp: 1;
    min-height: 1.4em;
  }

  .price-amount {
    font-size: 1.15rem;
  }

  .view-detail-btn {
    font-size: 0.8rem;
    padding: 0.55rem 0.75rem;
  }
}


</style>